import React, { FC, useCallback, useContext, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useDebounce } from 'utils/useDebounce'
import { ORGANIZATION_FIELD_NAME } from '../constants'
import { OrganizationOption } from '../OrganizationOption/OrganizationOption'
import { IOrganizationsItem } from '../../../interfaces/IOrganizationsItem'
import { IFormValue } from '../../../validator/IFormValues'
import { LanguageContext } from '../../LanguageContext/LanguageContext'
import { CreatableSelectField } from '../../CreatableSelectField/CreatableSelectField'
import { useGetOrganizations } from '../../../api/query/useGetOrganizations'

const DEFAULT_MIN_SEARCH_LENGTH = 3

const renderItem = (option: IOrganizationsItem, inputValue: IOrganizationsItem, isActive: boolean) => (
  <OrganizationOption isActive={isActive} option={option} />
)

const renderValue = (inputValue: IOrganizationsItem) => inputValue.name

type FormOrganizationFieldProps = {
  validate?: (name: string) => (currentValue: IFormValue) => Promise<string>
}

export const FormOrganizationField: FC<FormOrganizationFieldProps> = ({ validate }) => {
  const t = useTranslations()
  const [search, setSearch] = useState<string>('')
  const { language } = useContext(LanguageContext)
  const { data } = useGetOrganizations({ searchString: useDebounce(search), lang: language })
  const organizationFieldName = t(ORGANIZATION_FIELD_NAME)

  const onChange = useCallback((search: string): void => {
    if (search.length >= DEFAULT_MIN_SEARCH_LENGTH) {
      setSearch(search)
    }
  }, [])

  return (
    <CreatableSelectField<IOrganizationsItem>
      name="organization"
      label={organizationFieldName}
      options={data}
      onChange={onChange}
      renderItem={renderItem}
      renderValue={renderValue}
      minSearchLength={DEFAULT_MIN_SEARCH_LENGTH}
      validate={validate && validate('organization')}
    />
  )
}

FormOrganizationField.displayName = 'FormOrganizationField'
